import React from 'react';
import { Bill } from '../../types/bill';
import { formatCurrency } from '../../utils/formatters';

interface DeleteBillModalProps {
  bill: Bill;
  isOpen: boolean;
  isDeleting?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

const getStatusStyle = (status: string): React.CSSProperties => {
  const base: React.CSSProperties = {
    display: 'inline-block', padding: '2px 10px', borderRadius: 999,
    fontSize: 11, fontFamily: "'Inter', sans-serif", letterSpacing: '0.08em', fontWeight: 600,
  };
  switch (status) {
    case 'DRAFT':    return { ...base, background: 'rgba(120,120,140,0.18)', color: '#a0a0b8', border: '1px solid rgba(120,120,140,0.25)' };
    case 'PENDING':  return { ...base, background: 'rgba(92,78,51,0.22)', color: '#d97706', border: '1px solid rgba(92,78,51,0.35)' };
    case 'APPROVED': return { ...base, background: 'rgba(90,140,201,0.12)', color: '#7aacdc', border: '1px solid rgba(90,140,201,0.25)' };
    case 'PAID':     return { ...base, background: 'rgba(90,180,120,0.12)', color: '#6bbf8a', border: '1px solid rgba(90,180,120,0.25)' };
    case 'OVERDUE':  return { ...base, background: 'rgba(201,90,90,0.12)', color: '#c95a5a', border: '1px solid rgba(201,90,90,0.25)' };
    default:         return { ...base, background: 'rgba(201,90,90,0.12)', color: '#c95a5a', border: '1px solid rgba(201,90,90,0.25)' };
  }
};

export const DeleteBillModal: React.FC<DeleteBillModalProps> = ({
  bill, isOpen, isDeleting = false, onConfirm, onCancel,
}) => {
  if (!isOpen) return null;

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(7,7,15,0.85)', zIndex: 50, display: 'flex', alignItems: 'center', justifyContent: 'center', overflowY: 'auto' }}>
      <div style={{ position: 'relative', background: '#ffffff', border: '1px solid rgba(92,78,51,0.25)', borderRadius: 8, padding: '28px 32px', width: '100%', maxWidth: 420, boxShadow: '0 8px 40px rgba(0,0,0,0.6)' }}>
        <div style={{ position: 'absolute', top: 0, left: 32, right: 32, height: 1, background: 'linear-gradient(90deg, transparent, rgba(217,119,6,0.35), transparent)' }} />

        <div style={{ textAlign: 'center' }}>
          <div style={{ margin: '0 auto', display: 'flex', alignItems: 'center', justifyContent: 'center', width: 52, height: 52, borderRadius: '50%', background: 'rgba(201,90,90,0.12)', border: '1px solid rgba(201,90,90,0.3)' }}>
            <svg style={{ width: 24, height: 24, color: '#c95a5a' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
          </div>

          <h3 style={{ fontFamily: "'Inter', sans-serif", fontSize: 22, fontWeight: 600, color: '#1c2033', marginTop: 16, marginBottom: 0 }}>Delete Bill</h3>
          <p style={{ fontFamily: "'Inter', sans-serif", fontSize: 13, color: '#6b6a5c', marginTop: 10, marginBottom: 16 }}>
            Are you sure you want to delete this bill? This action cannot be undone.
          </p>

          <div style={{ background: '#faf7ee', border: '1px solid rgba(92,78,51,0.15)', borderRadius: 6, padding: '14px 16px' }}>
            {[
              { label: 'Bill Number', value: bill.billNumber },
              { label: 'Vendor', value: bill.vendor?.name },
            ].map(({ label, value }) => (
              <div key={label} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                <span style={{ fontFamily: "'Inter', sans-serif", fontSize: 9, letterSpacing: '0.22em', textTransform: 'uppercase', color: '#6b6a5c' }}>{label}</span>
                <span style={{ fontFamily: "'Inter', sans-serif", fontSize: 13, color: '#5b5a52' }}>{value}</span>
              </div>
            ))}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
              <span style={{ fontFamily: "'Inter', sans-serif", fontSize: 9, letterSpacing: '0.22em', textTransform: 'uppercase', color: '#6b6a5c' }}>Total Amount</span>
              <span style={{ fontFamily: "'Inter', sans-serif", fontSize: 15, color: '#d97706' }}>{formatCurrency(bill.total)}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 0 }}>
              <span style={{ fontFamily: "'Inter', sans-serif", fontSize: 9, letterSpacing: '0.22em', textTransform: 'uppercase', color: '#6b6a5c' }}>Status</span>
              <span style={getStatusStyle(bill.status)}>{bill.status}</span>
            </div>
            {bill.attachments && bill.attachments.length > 0 && (
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 10 }}>
                <span style={{ fontFamily: "'Inter', sans-serif", fontSize: 9, letterSpacing: '0.22em', textTransform: 'uppercase', color: '#6b6a5c' }}>Attachments</span>
                <span style={{ fontFamily: "'Inter', sans-serif", fontSize: 13, color: '#5b5a52' }}>{bill.attachments.length} file(s)</span>
              </div>
            )}
          </div>

          <div style={{ marginTop: 14, background: 'rgba(201,90,90,0.08)', border: '1px solid rgba(201,90,90,0.18)', borderRadius: 5, padding: '8px 12px', fontFamily: "'Inter', sans-serif", fontSize: 11, color: '#c95a5a' }}>
            All bill attachments will also be permanently deleted from the system.
          </div>
        </div>

        <div style={{ display: 'flex', justifyContent: 'center', gap: 12, marginTop: 22 }}>
          <button
            type="button" onClick={onCancel} disabled={isDeleting}
            style={{ padding: '8px 20px', background: 'transparent', border: '1px solid rgba(92,78,51,0.25)', borderRadius: 5, fontFamily: "'Inter', sans-serif", fontSize: 9, letterSpacing: '0.22em', textTransform: 'uppercase', color: '#6b6a5c', cursor: isDeleting ? 'not-allowed' : 'pointer', opacity: isDeleting ? 0.5 : 1 }}
          >
            Cancel
          </button>
          <button
            type="button" onClick={onConfirm} disabled={isDeleting}
            style={{ padding: '8px 20px', background: 'rgba(201,90,90,0.15)', border: '1px solid rgba(201,90,90,0.3)', borderRadius: 5, fontFamily: "'Inter', sans-serif", fontSize: 9, letterSpacing: '0.22em', textTransform: 'uppercase', color: '#c95a5a', cursor: isDeleting ? 'not-allowed' : 'pointer', opacity: isDeleting ? 0.7 : 1 }}
          >
            {isDeleting ? 'Deleting...' : 'Delete Bill'}
          </button>
        </div>
      </div>
    </div>
  );
};
